import { BackdropLoading } from '@/components/backdrop-loading'
import { ModalActions, ModalContainer } from '@/components/container/container'
import {
    AgendaBlock,
    AgendaBlockType,
    BLOCK_TYPE_OPTIONS,
    createAgendaBlock,
    updateAgendaBlock,
} from '@/lib/api/agenda-blocks/agenda-blocks-api'
import { formatISO, parseISO } from 'date-fns'
import { format, toDate } from 'date-fns-tz'
import React, { useEffect, useState } from 'react'
import { MdBlock } from 'react-icons/md'
import { toast } from 'react-toastify'
import { getBlockTypeColor } from '../../utils/status-colors'

const timeZone = 'America/Sao_Paulo'

interface BlockModalProps {
    agendaId: number
    block?: AgendaBlock | null
    initialDate?: Date
    onClose: () => void
    onSuccess: () => void
}

export function BlockModal({
    agendaId,
    block,
    initialDate,
    onClose,
    onSuccess,
}: BlockModalProps) {
    const isEdit = !!block

    const [loading, setLoading] = useState(false)
    const [type, setType] = useState<AgendaBlockType>('OTHER')
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [startDateStr, setStartDateStr] = useState('')
    const [startHour, setStartHour] = useState('')
    const [endDateStr, setEndDateStr] = useState('')
    const [endHour, setEndHour] = useState('')
    const [recurring, setRecurring] = useState(false)

    useEffect(() => {
        if (block) {
            setType(block.type)
            setTitle(block.title)
            setDescription(block.description || '')
            setRecurring(block.recurring)

            const start = parseISO(block.startDate)
            const end = parseISO(block.endDate)

            setStartDateStr(format(start, 'dd/MM/yyyy', { timeZone }))
            setStartHour(format(start, 'HH:mm', { timeZone }))
            setEndDateStr(format(end, 'dd/MM/yyyy', { timeZone }))
            setEndHour(format(end, 'HH:mm', { timeZone }))
        } else if (initialDate) {
            setStartDateStr(format(initialDate, 'dd/MM/yyyy', { timeZone }))
            setEndDateStr(format(initialDate, 'dd/MM/yyyy', { timeZone }))
            setStartHour('08:00')
            setEndHour('18:00')
        }
    }, [block, initialDate])

    const formatTime = (value: string): string => {
        const numbers = value.replace(/\D/g, '')
        const limited = numbers.slice(0, 4)
        if (limited.length <= 2) {
            return limited
        }
        return `${limited.slice(0, 2)}:${limited.slice(2, 4)}`
    }

    const handleSubmit = async () => {
        if (!title.trim()) {
            return toast.error('Título é obrigatório')
        }

        if (!startDateStr || !startHour) {
            return toast.error('Data e hora de início são obrigatórias')
        }

        if (!endDateStr || !endHour) {
            return toast.error('Data e hora de término são obrigatórias')
        }

        const startSplit = startDateStr.split('/')
        const endSplit = endDateStr.split('/')

        if (startSplit.length !== 3 || endSplit.length !== 3) {
            return toast.error('Formato de data inválido. Use DD/MM/AAAA')
        }

        const startStr = `${startSplit[2]}-${startSplit[1]}-${startSplit[0]} ${startHour}:00`
        const endStr = `${endSplit[2]}-${endSplit[1]}-${endSplit[0]} ${endHour}:00`

        const parsedStart = toDate(startStr, { timeZone })
        const parsedEnd = toDate(endStr, { timeZone })

        if (parsedEnd <= parsedStart) {
            return toast.error(
                'A data/hora de término deve ser posterior ao início'
            )
        }

        setLoading(true)
        try {
            const data = {
                agendaId,
                type,
                title: title.trim(),
                description: description.trim() || undefined,
                startDate: formatISO(parsedStart),
                endDate: formatISO(parsedEnd),
                recurring,
            }

            const response = isEdit
                ? await updateAgendaBlock(block.id, data)
                : await createAgendaBlock(data)

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Bloqueio atualizado com sucesso'
                        : 'Bloqueio criado com sucesso'
                )
                onSuccess()
                onClose()
            } else {
                toast.error(response.error || 'Erro ao salvar bloqueio')
            }
        } catch (error: any) {
            toast.error(error.message || 'Erro ao salvar bloqueio')
        } finally {
            setLoading(false)
        }
    }

    const colors = getBlockTypeColor(type)

    return (
        <React.Fragment>
            <dialog className="modal modal-open">
                <ModalContainer size="sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}
                        >
                            <MdBlock className={`text-xl ${colors.text}`} />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">
                                {isEdit ? 'Editar Bloqueio' : 'Novo Bloqueio'}
                            </h3>
                            <p className="text-sm text-base-content/60">
                                Bloqueie um horário na agenda
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo de Bloqueio
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={type}
                                onChange={e =>
                                    setType(e.target.value as AgendaBlockType)
                                }
                            >
                                {BLOCK_TYPE_OPTIONS.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Título *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Reunião de equipe"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Descrição
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações (opcional)"
                                rows={2}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Início
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="DD/MM/AAAA"
                                    value={startDateStr}
                                    onChange={e =>
                                        setStartDateStr(e.target.value)
                                    }
                                    required
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Hora Início
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="HH:MM"
                                    value={startHour}
                                    onChange={e =>
                                        setStartHour(formatTime(e.target.value))
                                    }
                                    maxLength={5}
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Término
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="DD/MM/AAAA"
                                    value={endDateStr}
                                    onChange={e => setEndDateStr(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Hora Término
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="HH:MM"
                                    value={endHour}
                                    onChange={e =>
                                        setEndHour(formatTime(e.target.value))
                                    }
                                    maxLength={5}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="cursor-pointer flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={recurring}
                                    onChange={e =>
                                        setRecurring(e.target.checked)
                                    }
                                />
                                <span className="label-text">
                                    Repetir semanalmente
                                </span>
                            </label>
                        </div>
                    </div>

                    <ModalActions>
                        <button className="btn btn-ghost" onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
                        </button>
                    </ModalActions>
                </ModalContainer>
            </dialog>

            <BackdropLoading visible={loading} />
        </React.Fragment>
    )
}
